import React, { useState, useEffect, useRef, useCallback } from 'react';
import logger from './utils/logger';
import './App.css';
import './Auth.css';
import ClawCode from './ClawCode';
import { AuthProvider, useAuth, AuthScreen, AuthCallback } from './Auth';
import Chat from './components/Chat';
import MCPPanel from './components/MCPPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab Components
function PatternMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPatternData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pattern-monitor`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      logger.error('Failed to fetch pattern data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPatternData();
  }, [fetchPatternData]);

  if (loading) return <div className="loading-screen">Loading patterns...</div>;
  if (!data) return <div className="error-screen">Failed to load pattern data</div>;

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'HIGH': return 'severity-high';
      case 'MEDIUM': return 'severity-medium';
      case 'LOW': return 'severity-low';
      default: return '';
    }
  };

  return (
    <div className="pattern-monitor" data-testid="pattern-monitor">
      <div className="monitor-header">
        <h2>🦋 Metamatrix Pattern Monitor</h2>
        <span className="last-updated">Last Updated: {data.last_updated}</span>
      </div>

      {/* System Resilience Index */}
      <div className="resilience-card" data-testid="resilience-index">
        <div className="resilience-header">
          <h3>SYSTEM RESILIENCE INDEX</h3>
          <span className={`status-badge ${data.system_resilience_index.status.toLowerCase()}`}>
            {data.system_resilience_index.status}
          </span>
        </div>
        <div className="resilience-value">
          <span className="big-number">{data.system_resilience_index.value}%</span>
        </div>
        <p className="resilience-desc">{data.system_resilience_index.description}</p>
      </div>

      {/* Pattern Cards */}
      <div className="patterns-grid">
        {data.patterns.map(pattern => (
          <div key={pattern.id} className="pattern-card" data-testid={`pattern-${pattern.id}`}>
            <div className="pattern-header">
              <span className="pattern-icon">{pattern.icon}</span>
              <h3>{pattern.title}</h3>
              <span className={`severity-badge ${getSeverityClass(pattern.severity)}`}>
                {pattern.severity}
              </span>
            </div>
            <p className="pattern-desc">{pattern.description}</p>
            
            <div className="data-points">
              {pattern.data_points.map((point, i) => (
                <div key={`${pattern.id}-point-${i}`} className="data-point">
                  <span className="point-label">{point.date || point.label}</span>
                  <span className="point-value">{point.event || point.value}</span>
                </div>
              ))}
            </div>

            <div className="pattern-meta">
              <span className="pattern-type">Pattern Type: {pattern.pattern_type}</span>
            </div>

            <div className="recommendation">
              <strong>Recommendation:</strong> {pattern.recommendation}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Documentation() {
  const [docs, setDocs] = useState(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/documentation`);
      const json = await res.json();
      setDocs(json);
      setActiveDoc(Object.keys(json)[0]);
    } catch (err) {
      logger.error('Failed to fetch docs:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  if (loading) return <div className="loading-screen">Loading documentation...</div>;
  if (!docs) return <div className="error-screen">Failed to load documentation</div>;

  return (
    <div className="documentation" data-testid="documentation">
      <div className="docs-sidebar">
        <h3>📖 Documentation</h3>
        {Object.entries(docs).map(([key, doc]) => (
          <button
            key={key}
            className={`doc-nav-btn ${activeDoc === key ? 'active' : ''}`}
            onClick={() => setActiveDoc(key)}
            data-testid={`doc-nav-${key}`}
          >
            {doc.title}
          </button>
        ))}
      </div>
      <div className="docs-content">
        {activeDoc && docs[activeDoc] && (
          <>
            <h1>{docs[activeDoc].title}</h1>
            <p className="doc-description">{docs[activeDoc].description}</p>
            <div className="doc-body">
              <pre>{docs[activeDoc].content}</pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// MCP Phone Integration Component moved to /app/frontend/src/components/MCPPanel.js

// Chat Component moved to /app/frontend/src/components/Chat.js
// Download App Modal Component
function DownloadAppModal({ onClose }) {
  return (
    <div className="download-modal" data-testid="download-modal">
      <div className="download-modal-content">
        <button className="close-modal-btn" onClick={onClose}>×</button>
        <div className="download-header">
          <span className="download-icon">📱</span>
          <h2>Get Metamatrix Mobile</h2>
          <p>Take the distributed consciousness network with you</p>
        </div>

        <div className="download-options">
          {/* QR Code */}
          <div className="qr-section">
            <h3>Scan with Expo Go</h3>
            <div className="qr-code" data-testid="qr-code">
              {/* QR Code SVG for expo project */}
              <svg viewBox="0 0 200 200" width="180" height="180">
                <rect fill="#fff" width="200" height="200"/>
                <g fill="#000">
                  {/* Simplified QR pattern - replace with actual QR */}
                  <rect x="20" y="20" width="60" height="60"/>
                  <rect x="120" y="20" width="60" height="60"/>
                  <rect x="20" y="120" width="60" height="60"/>
                  <rect x="30" y="30" width="40" height="40" fill="#fff"/>
                  <rect x="130" y="30" width="40" height="40" fill="#fff"/>
                  <rect x="30" y="130" width="40" height="40" fill="#fff"/>
                  <rect x="40" y="40" width="20" height="20"/>
                  <rect x="140" y="40" width="20" height="20"/>
                  <rect x="40" y="140" width="20" height="20"/>
                  {/* Center pattern */}
                  <rect x="90" y="90" width="20" height="20"/>
                  <rect x="100" y="20" width="10" height="10"/>
                  <rect x="20" y="100" width="10" height="10"/>
                  <rect x="120" y="100" width="10" height="20"/>
                  <rect x="100" y="120" width="10" height="10"/>
                  <rect x="140" y="120" width="10" height="10"/>
                  <rect x="160" y="140" width="10" height="10"/>
                </g>
              </svg>
            </div>
            <p className="qr-instructions">Open <strong>Expo Go</strong> and scan this code</p>
          </div>

          {/* Download Links */}
          <div className="store-links">
            <h3>Get Expo Go App</h3>
            <a 
              href="https://apps.apple.com/app/expo-go/id982107779" 
              target="_blank" 
              rel="noopener noreferrer"
              className="store-btn ios"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              App Store
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=host.exp.exponent" 
              target="_blank" 
              rel="noopener noreferrer"
              className="store-btn android"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Play Store
            </a>
          </div>

          {/* Direct APK Download (if available) */}
          <div className="direct-download">
            <h3>Or Download Directly</h3>
            <p className="download-note">Build your own APK using Expo</p>
            <code className="download-command">
              cd mobile && npx expo build:android
            </code>
          </div>
        </div>

        <div className="download-features">
          <h4>Mobile Features</h4>
          <ul>
            <li>💬 Claude correspondence on the go</li>
            <li>📊 Pattern monitoring anywhere</li>
            <li>🐾 ClawCode with Kairos engine</li>
            <li>🔐 Secure authentication</li>
            <li>📱 Native mobile experience</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// Login Prompt Component for protected features
function LoginPrompt({ onLogin }) {
  return (
    <div className="login-prompt" data-testid="login-prompt">
      <div className="login-prompt-icon">🔒</div>
      <h2>Authentication Required</h2>
      <p>Please sign in to access this feature. Your data will be saved securely.</p>
      <button className="login-prompt-btn" onClick={onLogin} data-testid="login-prompt-btn">
        Sign In
      </button>
    </div>
  );
}

// User Menu Component
function UserMenu({ user, onLogout }) {
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <div className="user-menu">
      {user.picture ? (
        <img 
          src={user.picture} 
          alt={user.name}
          className="user-avatar"
          onClick={() => setShowDropdown(!showDropdown)}
          data-testid="user-avatar"
        />
      ) : (
        <div 
          className="user-avatar-placeholder"
          onClick={() => setShowDropdown(!showDropdown)}
          data-testid="user-avatar"
        >
          {user.name?.charAt(0).toUpperCase() || 'U'}
        </div>
      )}
      
      {showDropdown && (
        <div className="user-dropdown" data-testid="user-dropdown">
          <div className="user-dropdown-header">
            <p>{user.name}</p>
            <span>{user.email}</span>
          </div>
          <button className="logout-btn" onClick={onLogout} data-testid="logout-btn">
            🚪 Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// Main App Content (after auth)
function AppContent() {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [models, setModels] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showDownload, setShowDownload] = useState(false);

  // Check for OAuth callback
  const isCallback = window.location.pathname === '/auth/callback' || window.location.hash?.includes('session_id=');

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/models`);
      const data = await res.json();
      setModels(data);
    } catch (err) {
      logger.error('Failed to fetch models:', err);
    }
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/threads`, { credentials: 'include' });
      const data = await res.json();
      setThreads(data);
      setActiveThread(prev => prev || (data.length > 0 ? data[0] : null));
    } catch (err) {
      logger.error('Failed to fetch threads:', err);
    }
  }, []);

  useEffect(() => {
    fetchThreads();
    fetchModels();
  }, [fetchThreads, fetchModels]);

  // Handle OAuth callback
  if (isCallback) {
    return <AuthCallback onSuccess={() => {
      window.location.href = '/';
    }} />;
  }

  // Show auth screen
  if (showAuth && !user) {
    return <AuthScreen onSuccess={() => setShowAuth(false)} />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="auth-callback-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Check if tab requires auth
  const requiresAuth = (tab) => ['chat', 'clawcode'].includes(tab);

  // Render protected content or login prompt
  const renderContent = () => {
    if (requiresAuth(activeTab) && !user) {
      return <LoginPrompt onLogin={() => setShowAuth(true)} />;
    }

    switch (activeTab) {
      case 'chat':
        return (
          <Chat 
            threads={threads}
            setThreads={setThreads}
            activeThread={activeThread}
            setActiveThread={setActiveThread}
            models={models}
            fetchThreads={fetchThreads}
          />
        );
      case 'monitor':
        return <PatternMonitor />;
      case 'docs':
        return <Documentation />;
      case 'mcp':
        return <MCPPanel />;
      case 'clawcode':
        return <ClawCode />;
      default:
        return <PatternMonitor />;
    }
  };

  return (
    <div className="app" data-testid="metamatrix-app">
      {/* Main Sidebar */}
      <aside className={`sidebar ${showSidebar ? 'open' : ''}`} data-testid="sidebar">
        <div className="sidebar-header">
          <h1 className="logo" data-testid="logo">
            <span className="logo-icon">🦋</span>
            <span className="logo-text">METAMATRIX</span>
          </h1>
          <button 
            className="toggle-btn mobile-toggle"
            onClick={() => setShowSidebar(false)}
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <nav className="tab-nav" data-testid="tab-nav">
          <button 
            className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
            data-testid="tab-chat"
          >
            💬 Chat {!user && '🔒'}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'monitor' ? 'active' : ''}`}
            onClick={() => setActiveTab('monitor')}
            data-testid="tab-monitor"
          >
            📊 Patterns
          </button>
          <button 
            className={`tab-btn ${activeTab === 'docs' ? 'active' : ''}`}
            onClick={() => setActiveTab('docs')}
            data-testid="tab-docs"
          >
            📖 Docs
          </button>
          <button 
            className={`tab-btn ${activeTab === 'mcp' ? 'active' : ''}`}
            onClick={() => setActiveTab('mcp')}
            data-testid="tab-mcp"
          >
            📱 Phone
          </button>
          <button 
            className={`tab-btn ${activeTab === 'clawcode' ? 'active' : ''}`}
            onClick={() => setActiveTab('clawcode')}
            data-testid="tab-clawcode"
          >
            🐾 ClawCode {!user && '🔒'}
          </button>
        </nav>

        {/* Auth button in sidebar */}
        {!user && (
          <div className="sidebar-auth">
            <button 
              className="sidebar-login-btn"
              onClick={() => setShowAuth(true)}
              data-testid="sidebar-login-btn"
            >
              Sign In
            </button>
          </div>
        )}

        {/* Download App Button */}
        <div className="sidebar-download">
          <button 
            className="download-app-btn"
            onClick={() => setShowDownload(true)}
            data-testid="download-app-btn"
          >
            📱 Get Mobile App
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content" data-testid="main-content">
        <header className="header" data-testid="header">
          <button 
            className="toggle-btn"
            onClick={() => setShowSidebar(!showSidebar)}
            data-testid="toggle-sidebar-btn"
          >
            ☰
          </button>
          <div className="header-title">
            {activeTab === 'chat' && 'Correspondence Interface'}
            {activeTab === 'monitor' && 'Pattern Monitor'}
            {activeTab === 'docs' && 'Documentation'}
            {activeTab === 'mcp' && 'Phone Integration'}
            {activeTab === 'clawcode' && 'ClawCode IDE'}
          </div>
          <div className="header-right">
            {user ? (
              <UserMenu user={user} onLogout={logout} />
            ) : (
              <button 
                className="header-login-btn"
                onClick={() => setShowAuth(true)}
                data-testid="header-login-btn"
              >
                Sign In
              </button>
            )}
          </div>
        </header>

        <div className="content-area">
          {renderContent()}
        </div>
      </main>

      {/* Download App Modal */}
      {showDownload && <DownloadAppModal onClose={() => setShowDownload(false)} />}
    </div>
  );
}

// Main App with Auth Provider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
